import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  Card,
  CardBody,
  Icon,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { Check, FileText, MapPin, Package } from "lucide-react";
import {
  statusColor,
  statusLabel,
  workflowStage,
  WORKFLOW_STEPS,
} from "../utils/marketplaceStatus";
import { money, quantity, dateTime } from "../utils/marketplaceFormatters";

export const StatusBadge = ({ status }) => (
  <Badge
    colorScheme={statusColor(status)}
    borderRadius="full"
    px="2.5"
    py="1"
    fontWeight="600"
    fontSize="xs"
  >
    {statusLabel(status)}
  </Badge>
);
export const MarketplacePageShell = ({
  title,
  subtitle,
  actions,
  children,
}) => (
  <Box maxW="1440px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 5, md: 8 }}>
    <Flex
      mb="8"
      gap="4"
      justify="space-between"
      align={{ base: "start", md: "center" }}
      direction={{ base: "column", md: "row" }}
    >
      <Box>
        <Heading size="lg" letterSpacing="-0.02em">{title}</Heading>
        {subtitle && (
          <Text mt="1" color="gray.500">
            {subtitle}
          </Text>
        )}
      </Box>
      <HStack flexWrap="wrap">{actions}</HStack>
    </Flex>
    {children}
  </Box>
);
export const MarketplaceKpiCard = ({ label, value, currency, icon, hint }) => (
  <Card bg={useColorModeValue("white", "gray.800")} _hover={{ boxShadow: "sm", transform: "translateY(-1px)" }} transition="all 150ms">
    <CardBody>
    <HStack justify="space-between" align="start">
      <Stat>
    <StatLabel color="gray.500">{label}</StatLabel>
    <StatNumber fontSize="2xl" mt="1">
      {currency ? money(value ?? 0) : (value ?? 0)}
    </StatNumber>
    {hint && <Text fontSize="xs" color="gray.500" mt="1">{hint}</Text>}
      </Stat>
      {icon && <Icon as={icon} boxSize="5" color="teal.500" />}
    </HStack>
    </CardBody>
  </Card>
);
export const MarketplaceEmptyState = ({
  message = "No records found.",
  action,
}) => (
  <VStack
    borderWidth="1px"
    bg={useColorModeValue("white", "gray.800")}
    borderStyle="dashed"
    borderRadius="lg"
    py="12"
    spacing="3"
  >
    <Text color="gray.500">{message}</Text>
    {action}
  </VStack>
);
export const MarketplaceSkeleton = ({ rows = 4 }) => (
  <Stack>
    {Array.from({ length: rows }, (_, i) => (
      <Skeleton key={i} height="70px" borderRadius="md" />
    ))}
  </Stack>
);
export const MarketplaceWorkflowStepper = ({ status }) => {
  const stage = workflowStage(status);
  return (
    <Flex overflowX="auto" py="2" gap="0">
      {WORKFLOW_STEPS.map((step, index) => (
        <Flex
          key={step}
          minW="92px"
          align="center"
          direction="column"
          color={index < stage ? "green.600" : index === stage ? "teal.700" : "gray.400"}
        >
          <Box
            w="8"
            h="8"
            borderRadius="full"
            bg={index < stage ? "green.500" : index === stage ? "teal.500" : "gray.200"}
            color="white"
            textAlign="center"
            lineHeight="8"
          >
            {index < stage ? <Icon as={Check} boxSize="4" mt="2" /> : index + 1}
          </Box>
          <Text mt="1" fontSize="xs" textAlign="center">
            {step}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};
export const MaterialSummaryCard = ({ listing = {} }) => (
  <Card><CardBody>
    <HStack justify="space-between" align="start">
      <Box>
        <Heading size="sm">
          {listing.materialName || listing.name || "Material"}
        </Heading>
        <Text fontSize="sm" color="gray.500">
          {listing.listingNumber || listing.listingId || listing._id}
        </Text>
      </Box>
      <StatusBadge status={listing.status} />
    </HStack>
    <SimpleGrid columns="2" spacing="3" mt="4">
      <Info
        label="Available"
        value={quantity(
          listing.availableQuantity ?? listing.quantity,
          listing.uom,
        )}
      />
      <Info
        label="Seller rate"
        value={money(
          listing.sellerAskingRate ?? listing.askingRate,
          listing.currency,
        )}
      />
      <Info label="Category" value={listing.category || "—"} />
      <Info
        label="Location"
        value={listing.location?.city || listing.location || "—"}
      />
    </SimpleGrid>
  </CardBody></Card>
);
export const SellerSummaryCard = ({ seller = {} }) => (
  <Card><CardBody>
    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
      Seller
    </Text>
    <Heading size="sm" mt="1">
      {seller.companyName || seller.name || "—"}
    </Heading>
    <Text fontSize="sm" color="gray.500">
      {seller.location || seller.email || ""}
    </Text>
    {seller.verified && (
      <Badge mt="2" colorScheme="green">
        Verified seller
      </Badge>
    )}
  </CardBody></Card>
);
export const MaterialImageGallery = ({ images = [], materialName = "material" }) => {
  const normalized = images.map((image) => typeof image === "string" ? { url: image } : image).filter((image) => image?.url);
  return normalized.length ? (
    <SimpleGrid columns={{ base: 1, sm: Math.min(normalized.length, 3) }} spacing="2">
      {normalized.map((image, index) => (
        <Image
          key={image.url || index}
          h="180px"
          w="100%"
          borderRadius="md"
          objectFit="cover"
          src={image.url}
          alt={`${materialName} image ${index + 1}`}
          fallback={<Flex h="180px" align="center" justify="center" color="gray.400" bg="gray.50"><Icon as={Package} boxSize="10" /></Flex>}
        />
      ))}
    </SimpleGrid>
  ) : (
    <Flex h="180px" align="center" justify="center" borderWidth="1px" borderRadius="lg" color="gray.400" bg="gray.50">
      <Stack align="center" spacing="2"><Icon as={Package} boxSize="8" /><Text fontSize="sm">No images available</Text></Stack>
    </Flex>
  );
};
export const Info = ({ label, value }) => (
  <Box>
    <Text color="gray.500" fontSize="xs">
      {label}
    </Text>
    <Text fontWeight="medium" fontSize="sm">
      {value ?? "—"}
    </Text>
  </Box>
);
export const MaterialListingCard = ({ listing }) => (
  <Flex
    borderWidth="1px"
    borderRadius="xl"
    bg={useColorModeValue("white", "gray.800")}
    _hover={{ boxShadow: "sm", borderColor: "teal.200" }}
    transition="all 150ms"
    p="4"
    gap="4"
    align="center"
    direction={{ base: "column", sm: "row" }}
  >
    <Image
      boxSize={{ base: "100%", sm: "120px" }}
      h={{ base: "160px", sm: "96px" }}
      objectFit="cover"
      borderRadius="md"
      src={listing.images?.[0]?.url || listing.images?.[0]}
      alt=""
      fallback={<Flex w={{ base: "100%", sm: "120px" }} h={{ base: "160px", sm: "96px" }} align="center" justify="center" bg="gray.50" color="gray.400" borderRadius="md"><Icon as={Package} boxSize="8" /></Flex>}
    />
    <Box flex="1">
      <HStack justify="space-between">
        <Heading size="sm">{listing.materialName || listing.name}</Heading>
        <StatusBadge status={listing.status} />
      </HStack>
      <Text color="gray.500" fontSize="sm">
        {listing.seller?.companyName || listing.sellerName || "Seller"} ·{" "}
        {listing.location?.city || listing.location || "—"}
      </Text>
      <HStack mt="2" spacing="2" wrap="wrap">
        {[listing.composition, listing.color, listing.gsm && `${listing.gsm} GSM`, listing.width && `${listing.width}\"`, listing.grade && `Grade ${listing.grade}`]
          .filter(Boolean).slice(0, 4).map((item) => <Badge key={item} variant="subtle" colorScheme="gray">{item}</Badge>)}
      </HStack>
      <HStack mt="3" spacing="8">
        <Info
          label="Available"
          value={quantity(
            listing.availableQuantity ?? listing.quantity,
            listing.uom,
          )}
        />
        <Info
          label="Seller rate"
          value={money(
            listing.sellerAskingRate ?? listing.askingRate,
            listing.currency,
          )}
        />
      </HStack>
      <HStack mt="2" color="gray.500" fontSize="xs"><Icon as={MapPin} boxSize="3.5" /><Text>{listing.location?.city || listing.location || "Location not provided"}</Text></HStack>
    </Box>
    <Button
      as={Link}
      to={`/novatra/marketplace/listings/${listing._id || listing.id}`}
      size="sm"
      colorScheme="blue"
    >
      View details
    </Button>
  </Flex>
);
export const MaterialListingTable = ({ listings = [], actions }) =>
  listings.length ? (
    <Box overflowX="auto" borderWidth="1px" borderRadius="xl" bg={useColorModeValue("white", "gray.800")}>
      <Table size="sm" sx={{ "th": { bg: "gray.50", color: "gray.500", fontSize: "xs", textTransform: "uppercase", letterSpacing: "0.04em" }, "td": { py: 4 } }}>
        <Thead>
          <Tr>
            <Th>Listing ID</Th>
            <Th>Material</Th>
            <Th>Available Qty</Th>
            <Th>Seller Rate</Th>
            <Th>Status</Th>
            <Th>Updated</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {listings.map((item) => (
            <Tr key={item._id || item.id}>
              <Td>{item.listingNumber || item.listingId || "—"}</Td>
              <Td fontWeight="medium">{item.materialName || item.name}</Td>
              <Td>
                {quantity(item.availableQuantity ?? item.quantity, item.uom)}
              </Td>
              <Td>
                {money(item.sellerAskingRate ?? item.askingRate, item.currency)}
              </Td>
              <Td>
                <StatusBadge status={item.status} />
              </Td>
              <Td>{dateTime(item.updatedAt || item.createdAt)}</Td>
              <Td>
                {actions?.(item) || (
                  <Button
                    as={Link}
                    to={`/novatra/marketplace/listings/${item._id || item.id}`}
                    size="xs"
                  >
                    View
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  ) : (
    <MarketplaceEmptyState message="No material listings found." />
  );
export const MaterialSpecificationTable = ({ specifications = {} }) => (
  <Box overflowX="auto">
    <Table size="sm">
      <Tbody>
        {Object.entries(specifications)
          .filter(([key, value]) => !["_id", "id", "tenantId", "createdBy", "updatedBy", "__v", "statusHistory", "materialMapping", "images", "documents", "seller", "location"].includes(key) && value !== null && value !== "" && typeof value !== "object")
          .map(([key, value]) => (
            <Tr key={key}>
              <Td color="gray.500" textTransform="capitalize">
                {key.replace(/([A-Z])/g, " $1")}
              </Td>
              <Td>{String(value)}</Td>
            </Tr>
          ))}
      </Tbody>
    </Table>
  </Box>
);
export const MaterialDocuments = ({ documents = [] }) =>
  documents.length ? (
    <VStack align="stretch">
      {documents.map((document, i) => (
        <Button
          key={i}
          as="a"
          href={document.url || document}
          target="_blank"
          variant="outline"
          justifyContent="start"
          leftIcon={<Icon as={FileText} />}
        >
          {document.name || `Document ${i + 1}`}
        </Button>
      ))}
    </VStack>
  ) : (
    <Text color="gray.500">No documents attached.</Text>
  );
